import React, { Fragment, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  CircularProgress,
} from "@mui/material";
import { t } from "i18next";
import "./SubscriptionSettings.scss";
import SubscriptionLandingPage from "../Subscription/SubscriptionLandingPage";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useDispatch, useSelector } from "react-redux";
import { dateParserWithoutTime, validateInputNumber } from "../../helper";
import { neutral } from "../../constants/colorConstants";
import {
  getAddons,
  getSubscriptionPlans,
  getSubscriptionTransactions,
  increaseAddon,
  paymentRequest,
} from "../../services/subscriptionService";
import { SUBSCRIPTION_REDUCER } from "../../constants/enums";
import UpgradePlanDialog from "../Subscription/UpgradePlanDialog";
import UpgradeConfirmationDialog from "../Subscription/UpgradeConfirmationDialog";
import UpgradeSuccessDialog from "../Subscription/UpgradeSuccessDialog";
import UpgradeMessageDialog from "../Subscription/UpgradeMessageDialog";

const SubscriptionSettings = () => {
  const dispatch = useDispatch();
  const subscription = useSelector((state: any) => state.sub.subscription);
  const cardDetailsStatus = useSelector(
    (state: any) => state.sub.cardDetailsStatus
  );
  const fromZohoPaymetntsPage = useSelector(
    (state: any) => state.sub.fromZohoPaymetntsPage
  );
  const addonPermissions = useSelector(
    (state: any) => state.sub.addonPermissions
  );
  const companyPreferences = useSelector(
    (state: any) => state.org.companyPreferences
  );

  const baseCurrency = "USD";
  let transactionsCellColumns = [
    "Payment Date",
    "Plan / Addon",
    "Units",
    "Price",
  ];
  let addonsCellColumns = ["Name", "Description", "Units", "Price"];
  let addonsPurchaseCellColumns = [
    "Name",
    "Description",
    "Price",
    "Purchase units",
    "Total price",
  ];
  const [isaddAddonsState, setIsAddAddonsState] = useState(false);
  const [openUpgradePlanDialog, setOpenUpgradePlanDialog] = useState(false);
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [previousTransactions, setTransactions] = useState<any>([]);
  const [currentPlan, setCurrentPlan] = useState<any>({});
  const [allPlans, setAllPlans] = useState<any>([]);
  const [availableAddons, setAvailableAddons] = useState<any>([]);
  const [purchaseAddons, setPurchaseAddons] = useState<any>([]);
  const [allAddons, setAllAddons] = useState<any>([]);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [openUpgradeConfirmationDialog, setOpenUpgradeConfirmationDialog] =
    useState<any>(false);
  const [openUpgradeSuccessDialog, setOpenUpgradeSuccessDialog] =
    useState<any>(false);
  const [isCustomPlan, setIsCustomPlan] = useState(false);
  const [openUpgradeMessageDialog, setOpenUpgradeMessageDialog] =
    useState<any>(false);

  const handleUpgradePlanClose = () => {
    setOpenUpgradePlanDialog(false);
  };

  const handleSubscriptionClose = () => {
    setOpenSubscriptionDialog(false);
  };

  const handleNewAddons = () => {
    const purchaseAddonsList: any[] = [];
    availableAddons.forEach((eachAddon: any) => {
      purchaseAddonsList.push({
        addon_code: eachAddon.addon_code,
        addon_id: eachAddon.addon_id,
        description: eachAddon.description,
        name: eachAddon.name,
        type_formatted: eachAddon.type_formatted,
        price: eachAddon.price_brackets?.[0]?.price,
        purchase_quantity: null,
      });
    });
    setPurchaseAddons(purchaseAddonsList);
    setIsAddAddonsState(true);
  };

  const onChangePurchaseUnits = (e: any, index: number) => {
    if (!isNaN(e.target.value)) {
      purchaseAddons[index].purchase_quantity = e.target.value
        ? parseInt(e.target.value)
        : null;
      setPurchaseAddons([...purchaseAddons]);
    }
  };

  const handleProceedAddons = () => {
    setOpenUpgradeConfirmationDialog(false);
    const purchaseAddonsList: any[] = [];
    purchaseAddons.forEach((eachAddon: any) => {
      if (eachAddon.purchase_quantity) {
        purchaseAddonsList.push({
          addonCode: eachAddon.addon_code,
          purchaseType: eachAddon.type_formatted,
          purchaseQuantity: eachAddon.purchase_quantity,
        });
      }
    });
    if (purchaseAddonsList?.length) {
      increaseAddon({ purchaseAddonsList }).then((res) => {
        if (
          res?.data?.status === "success" &&
          res?.data?.addonPermissions?.length
        ) {
          setOpenUpgradeSuccessDialog(true);
          dispatch({
            type: SUBSCRIPTION_REDUCER.SET_ADDON_PERMISSIONS,
            payload: res.data.addonPermissions,
          });
          setIsAddAddonsState(false);
        }
      });
    }
  };

  const onClickPayment = () => {
    paymentRequest().then((res: any) => {
      let data = res?.data?.data;
      let paymentUrl = data?.url;
      if (paymentUrl) {
        window.open(paymentUrl, "_self");
      }
    });
  };

  useEffect(() => {
    getSubscriptionPlans().then((res: any) => {
      if (res?.data?.plans?.length) {
        res.data.plans.forEach((eachPlan: any) => {
          if (subscription?.planCode === eachPlan.plan_code) {
            setCurrentPlan(eachPlan);
          }
        });
        setAllPlans(res.data.plans);
      }
    });
    if (subscription?.nextBillingAt) {
      const currentDate = new Date();
      const currentTermEndAt = new Date(subscription?.nextBillingAt);
      const timeDifference = currentTermEndAt.getTime() - currentDate.getTime();
      const daysRemaining = Math.floor(timeDifference / (1000 * 3600 * 24)) + 1;
      setDaysRemaining(Math.max(0, daysRemaining));
    }
    if (
      subscription?.planCode &&
      !(
        subscription?.planCode.includes("v1-basic") ||
        subscription?.planCode.includes("v1-essential") ||
        subscription?.planCode.includes("v1-advanced")
      )
    ) {
      setIsCustomPlan(true);
    }
  }, [subscription]);

  useEffect(() => {
    if (subscription?.status !== "unsubscribed") {
      getSubscriptionTransactions().then((res: any) => {
        if (res?.data?.length) {
          // const transactionsSet = new Set(); // Having duplicate transactions due to multiple webhooks trigger for same event.
          const transactionsList: {
            id: any;
            startDate: string;
            endDate: string;
            plan: any;
            amount: string;
          }[] = [];
          res?.data.forEach((eachTransaction: any) => {
            let eachTransactionMap: any = {
              id: eachTransaction.id,
              paymentDate: eachTransaction?.paymentDate
                ? dateParserWithoutTime(eachTransaction.paymentDate)
                : "-",
              planCode: eachTransaction.planCode,
              planName: eachTransaction.planName,
              quantity: eachTransaction.quantity,
              amount: eachTransaction.isTrail
                ? "Free trial"
                : eachTransaction.currencySymbol + " " + eachTransaction.amount,
            };
            // if (
            //   !eachTransactionMap.endDate ||
            //   !transactionsSet.has(eachTransactionMap.endDate)
            // ) {
            transactionsList.push(eachTransactionMap);
            // transactionsSet.add(eachTransactionMap.endDate);
            // }
          });
          setTransactions(transactionsList);
        }
      });
    }
  }, [subscription]);

  useEffect(() => {
    if (subscription?.status !== "unsubscribed") {
      getAddons().then((res: any) => {
        if (res?.data?.addons?.length) {
          setAllAddons(
            res?.data?.addons.filter(
              (eachAddon: any) => eachAddon.status === "active"
            )
          );
        }
      });
    }
  }, [subscription]);

  useEffect(() => {
    if (allAddons.length && subscription && Object.keys(subscription).length) {
      const addons: any[] = [];
      allAddons.forEach((eachAddon: any) => {
        if (
          eachAddon?.addon_code?.length &&
          (eachAddon.addon_code.includes("v1") ||
            eachAddon.addon_code.includes("t1")) &&
          eachAddon?.plans?.length &&
          eachAddon.plans.findIndex(
            (eachPlan: any) => eachPlan.plan_code === subscription?.planCode
          ) > -1
        ) {
          if (addonPermissions?.length) {
            const permissionIndex = addonPermissions.findIndex(
              (eachAddonPermission: any) =>
                eachAddonPermission.addon_code === eachAddon.addon_code
            );
            eachAddon["quantity"] =
              permissionIndex > -1
                ? addonPermissions[permissionIndex].quantity
                : 0;
          }
          addons.push(eachAddon);
        }
      });
      setAvailableAddons(addons);
    }
  }, [allAddons, addonPermissions]);

  // useEffect(() => {
  //   if (previousTransactions.length && allPlans.length) {
  //     previousTransactions.forEach((eachTransaction: any) => {
  //       const planIndex = allPlans.findIndex(
  //         (eachPlan: any) => eachPlan.plan_code === eachTransaction.planCode
  //       );
  //       if (planIndex > -1) {
  //         eachTransaction.plan = allPlans[planIndex].name;
  //       }
  //     });
  //   }
  // }, [allPlans, previousTransactions]);

  return (
    <Fragment>
      <div className="w-100 h-full p-8 bg-slate-100 flex-col justify-start items-start gap-8 inline-flex overflow-y-auto">
        {!subscription || subscription?.status !== "unsubscribed" ? (
          <div className="w-1/2 p-6 my-4 bg-white rounded-lg shadow flex-col justify-start items-start gap-6 inline-flex">
            <div className="self-stretch pb-4 justify-start items-start gap-2 inline-flex">
              <div className="grow shrink basis-0 h-7 justify-between items-center flex">
                <div className="h-6 justify-start items-center gap-2 flex">
                  <div className="text-neutral-800 text-base font-normal font-['Proxima Nova']">
                    Current plan
                  </div>
                  {/* <div className="text-neutral-800 text-2xl font-semibold normal-case font-['Proxima Nova']">
                    {currentPlan?.name}
                  </div> */}
                </div>
                <div className="justify-start items-center gap-4 flex">
                  {subscription?.status !== "trial" ? (
                    <div>
                      <div
                        className="w-20 rounded border pw-bunit-button-primary pw-bunit-button-next text-white  font-bold bg-primary justify-center items-center flex cursor-pointer"
                        onClick={() => {
                          isCustomPlan
                            ? setOpenUpgradeMessageDialog(true)
                            : setOpenUpgradePlanDialog(true);
                        }}
                      >
                        <div className="px-4 justify-center text-xs items-center gap-2 font-['Proxima Nova'] flex">
                          Upgrade
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {!subscription?.cardProvided ? (
                        <div
                          className="w-28 bg-white rounded border border-green-400 justify-center items-center flex cursor-pointer"
                          // onClick={() => {
                          //   setOpenSubscriptionDialog(true);
                          // }}
                          onClick={onClickPayment}
                        >
                          <div className="w-28 py-1 justify-center items-center gap-2 flex">
                            <div className="text-green-400 text-xs font-bold font-['Proxima Nova']">
                              Add card
                            </div>
                          </div>
                        </div>
                      ) : (
                        ""
                      )}{" "}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="self-stretch justify-start items-start gap-2 inline-flex">
              <div className="flex-col justify-start items-start gap-6 inline-flex">
                <div className="flex-col justify-start items-start gap-6 flex">
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Plan")}:
                    </div>
                    <div className="self-stretch justify-start items-center gap-2 inline-flex">
                      <div className="text-neutral-800 text-base font-bold capitalize font-['Proxima Nova']">
                        {currentPlan?.name}
                      </div>
                      <div
                        className="text-green-400 text-xs font-bold font-['Proxima Nova'] cursor-pointer"
                        onClick={() => {
                          setOpenSubscriptionDialog(true);
                        }}
                      >
                        View Plan
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Price")}:
                    </div>
                    {currentPlan?.recurring_price !== null ? (
                      <div className="text-neutral-800 text-sm">
                        <span className="font-bold">
                          {baseCurrency +
                            " " +
                            (currentPlan.recurring_price || " - ")}
                        </span>
                        <span>
                          {" Paid " +
                            (currentPlan.shipping_interval_unit === "months"
                              ? "monthly"
                              : "yearly")}
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Account Type")}:
                    </div>
                    <div className="text-neutral-800 text-sm font-normal">
                      {companyPreferences?.account_type}
                    </div>
                  </div>
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Card Details")}:
                    </div>
                    <div className="self-stretch justify-start items-center gap-2 inline-flex">
                      <div className="text-neutral-800 text-sm font-normal">
                        {subscription?.cardProvided
                          ? "Provided"
                          : fromZohoPaymetntsPage
                          ? "Processing"
                          : cardDetailsStatus}
                      </div>
                      {!subscription?.cardProvided ? (
                        <div
                          className="text-green-400 text-xs font-bold font-['Proxima Nova'] cursor-pointer"
                          onClick={onClickPayment}
                        >
                          Add card
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                  {subscription?.cardProvided && subscription?.cardNumber ? (
                    <div className="flex flex-row gap-8">
                      <div className=" w-32 text-slate-500 text-sm font-normal">
                        {t("Payment")}:
                      </div>
                      <div className="text-neutral-800 text-sm font-normal">
                        {"XXXX XXXX " + subscription?.cardNumber}
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Term behaviour")}:
                    </div>
                    <div className="text-neutral-800 text-sm font-normal">
                      {"Auto renewing"}
                    </div>
                  </div>
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Started on")}:
                    </div>
                    <div className="text-neutral-800 text-sm font-normal">
                      {subscription?.currentTermStartsAt
                        ? dateParserWithoutTime(
                            subscription.currentTermStartsAt
                          )
                        : ""}
                    </div>
                  </div>
                  <div className="flex flex-row gap-8">
                    <div className=" w-32 text-slate-500 text-sm font-normal">
                      {t("Next Renewal")}:
                    </div>
                    <div className="text-neutral-800">
                      <span className="text-sm font-normal">
                        {subscription?.nextBillingAt
                          ? dateParserWithoutTime(subscription.nextBillingAt)
                          : ""}
                      </span>
                      {daysRemaining != null && daysRemaining >= 0 ? (
                        <span className="ml-2 p-1 bg-rose-100 font-bold text-xs rounded-lg text-rose-500">
                          {daysRemaining} days left
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-1/2 p-6 my-4 bg-white rounded-lg shadow flex-col justify-start items-start inline-flex">
            <div className="self-stretch justify-start items-start gap-2 inline-flex">
              <div className="grow shrink basis-0 justify-between items-center flex">
                <div className="justify-start items-center gap-2 flex">
                  <div className="text-neutral-800 text-base font-normal font-['Proxima Nova']">
                    Subscribe
                  </div>
                </div>
                <div className="justify-start items-center gap-4 flex">
                  <div>
                    <div
                      className="w-24 rounded border pw-bunit-button-primary pw-bunit-button-next text-white  font-bold bg-primary justify-center items-center flex cursor-pointer"
                      onClick={() => {
                        isCustomPlan
                          ? setOpenUpgradeMessageDialog(true)
                          : setOpenUpgradePlanDialog(true);
                      }}
                    >
                      <div className="px-4 justify-center text-xs truncate items-center gap-2 font-['Proxima Nova'] flex">
                        View plans
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {availableAddons?.length && subscription?.status === "live" ? (
          <>
            {!isaddAddonsState ? (
              <div className="self-stretch grow shrink basis-0 flex-col justify-start items-start gap-2 flex bg-white p-6 rounded-lg shadow ">
                <div className="self-stretch flex flex-row justify-start items-start gap-2 w-full justify-between items-center">
                  <div className="text-slate-400 text-sm font-bold font-['Proxima Nova']">
                    ADDONS
                  </div>
                  <div className="pw-add-new-bunit" onClick={handleNewAddons}>
                    <AddBoxIcon className="pw-box-icon" />{" "}
                    <span className="text-sm font-bold">{t("Addon")}</span>
                  </div>
                </div>
                <div className="flex flex-row py-4 w-full">
                  <Table className="border border-grey">
                    <TableHead className="bg-neutral-50">
                      <TableRow>
                        {addonsCellColumns.map((column) => (
                          <TableCell key={column}>{column}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody className="bg-white">
                      {availableAddons.map((eachAddon: any, index: number) => (
                        <TableRow key={eachAddon.addon_id}>
                          <TableCell>{eachAddon.name}</TableCell>
                          <TableCell>{eachAddon.description}</TableCell>
                          <TableCell>{eachAddon.quantity || 0}</TableCell>
                          <TableCell>
                            {baseCurrency +
                              " " +
                              eachAddon.price_brackets?.[0]?.price}{" "}
                            ({eachAddon.type_formatted})
                          </TableCell>
                          {/* <TableCell>
                            <div className="flex flex-row items-center gap-6">
                              <button
                                className={`pw-bunit-button-primary pw-bunit-button-next text-white  font-bold ${
                                  isAddonBuyNowLoading === eachAddon.addon_code
                                    ? "text-black bg-neutral-300"
                                    : "cursor-pointer bg-primary"
                                }`}
                                onClick={() => handleAddonBuyNow(eachAddon.addon_code)}
                              >
                                {isAddonBuyNowLoading === eachAddon.addon_code ? (
                                  <div className="flex flex-row gap-2 p-2">
                                    <CircularProgress size={20} />
                                  </div>
                                ) : (
                                  <span>{t("Buy Now")}</span>
                                )}
                              </button>
                              <div 
                                className="cursor-pointer"
                                onClick={() => {setOpenRemoveAddonAction(index ? "USER" : "FACILITY"); setOpenRemoveAddonDialog(true); setOpenRemoveAddonCode(eachAddon.addon_code)}}
                              >
                                Remove
                              </div>
                            </div>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="self-stretch grow shrink basis-0 flex-col justify-start items-start gap-2 flex bg-white p-6 rounded-lg shadow ">
                <div className="self-stretch flex flex-row justify-start items-start gap-2 w-full justify-between items-center">
                  <div className="text-slate-400 text-sm font-bold font-['Proxima Nova']">
                    ADDON DETAILS
                  </div>
                  <div className="flex flex-row gap-8 justify-start items-center cursor-pointer">
                    <div
                      className="text-sm bg-white font-bold"
                      onClick={() => {
                        setIsAddAddonsState(false);
                      }}
                    >
                      Cancel
                    </div>
                    <div
                      className="pw-add-new-bunit"
                      onClick={() => {
                        setOpenUpgradeConfirmationDialog(true);
                      }}
                    >
                      <span className="text-sm font-bold">{t("Proceed")}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row py-4 w-full">
                  <Table className="border border-grey">
                    <TableHead className="bg-neutral-50">
                      <TableRow>
                        {addonsPurchaseCellColumns.map((column) => (
                          <TableCell key={column}>{column}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody className="bg-white">
                      {purchaseAddons.map((eachAddon: any, index: number) => (
                        <TableRow key={eachAddon.addon_id}>
                          <TableCell>{eachAddon.name}</TableCell>
                          <TableCell>{eachAddon.description}</TableCell>
                          <TableCell>
                            {baseCurrency + " " + eachAddon.price} (
                            {eachAddon.type_formatted})
                          </TableCell>
                          <TableCell>
                            <div className="w-12">
                              <TextField
                                sx={{ input: { textAlign: "right" } }}
                                type="number"
                                variant="standard"
                                placeholder="units"
                                size="small"
                                name="units"
                                value={eachAddon.purchase_quantity}
                                onKeyDown={validateInputNumber}
                                onChange={(e) =>
                                  onChangePurchaseUnits(e, index)
                                }
                                className="pw-width-100 pw-input-field pw-input-text pw-activity-input pw-addons-input-qty"
                                InputProps={{
                                  style: {
                                    color: neutral[900],
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            {baseCurrency +
                              " " +
                              eachAddon.purchase_quantity * eachAddon.price}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </>
        ) : (
          ""
        )}
        {previousTransactions?.length ? (
          <div className="self-stretch grow shrink basis-0 flex-col justify-start items-start gap-2 flex">
            <div className="self-stretch h-3 flex-col justify-start items-start gap-2 flex">
              <div className="text-slate-500 text-xs font-bold font-['Proxima Nova']">
                Previous transactions
              </div>
            </div>
            <div className="flex flex-row py-4 w-full">
              <Table>
                <TableHead className="bg-neutral-50">
                  <TableRow>
                    {transactionsCellColumns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody className="bg-white">
                  {previousTransactions.map((each: any) => (
                    <TableRow key={each.id}>
                      <TableCell>{each.paymentDate}</TableCell>
                      {/* <TableCell className="capitalize">{each.plan}</TableCell> */}
                      <TableCell className="capitalize">
                        {each.planName}
                      </TableCell>
                      <TableCell>{each.quantity}</TableCell>
                      <TableCell>{each.amount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
      {openSubscriptionDialog && (
        <SubscriptionLandingPage
          open={openSubscriptionDialog}
          onClose={handleSubscriptionClose}
          showCloseIcon={true}
        />
      )}
      {openUpgradePlanDialog && (
        <UpgradePlanDialog
          open={openUpgradePlanDialog}
          onClose={handleUpgradePlanClose}
        />
      )}
      {openUpgradeConfirmationDialog && (
        <UpgradeConfirmationDialog
          open={openUpgradeConfirmationDialog}
          onClose={() => {
            setOpenUpgradeConfirmationDialog(false);
          }}
          onConfirm={handleProceedAddons}
          type={"addon"}
        />
      )}
      {openUpgradeSuccessDialog && (
        <UpgradeSuccessDialog
          open={openUpgradeSuccessDialog}
          onContinue={() => {
            setOpenUpgradeSuccessDialog(false);
          }}
          type={"addon"}
        />
      )}
      {openUpgradeMessageDialog && (
        <UpgradeMessageDialog
          open={openUpgradeMessageDialog}
          onClose={() => {
            setOpenUpgradeMessageDialog(false);
          }}
          fromUpgradePlanDialog={true}
        />
      )}
    </Fragment>
  );
};

export default SubscriptionSettings;
